<p-toast></p-toast>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet Mobilization"
        title="Mobilization Entries"
        subtitle="Search and page through driver, vehicle, project, site, and lifecycle status records.">
        <div nav>
            <p-button label="Overview" icon="pi pi-chart-line" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization"></p-button>
            <p-button label="Entries" icon="pi pi-list" routerLink="/fleet/mobilization/entries"></p-button>
            <p-button label="Approvals" icon="pi pi-check-square" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/change-requests"></p-button>
            <p-button label="History" icon="pi pi-history" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/history"></p-button>
        </div>
        <div actions>
            <p-button label="New Mobilization" icon="pi pi-plus" routerLink="/fleet/mobilization/new"></p-button>
        </div>
    </app-page-hero>

    <app-page-search-panel
        title="Entry Search"
        [activeFilters]="activeFilterCount()"
        [resultText]="totalRecords + ' records found'"
        [collapsed]="false"
        (search)="search()"
        (clear)="clearSearch()">
        <div class="search-grid">
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <input id="entry-keyword" pInputText [(ngModel)]="keyword" placeholder="Driver, vehicle, project, site" />
                        <label for="entry-keyword">Keyword</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="entry-status" [options]="statusOptions" [(ngModel)]="selectedStatus" optionLabel="label" optionValue="value" [showClear]="true" placeholder="Any status" styleClass="w-full"></p-select>
                        <label for="entry-status">Status</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-datePicker inputId="entry-dates" [(ngModel)]="dateRange" selectionMode="range" [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body" styleClass="w-full"></p-datePicker>
                        <label for="entry-dates">Start Date Range</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-truck"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="entry-vehicle-type" [options]="vehicleTypes" [(ngModel)]="vehicleTypeId" optionLabel="name" optionValue="id"
                            [showClear]="true" [filter]="true" filterBy="name" placeholder="Any truck type" appendTo="body" styleClass="w-full"></p-select>
                        <label for="entry-vehicle-type">Truck Type</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-ticket"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <p-select inputId="entry-trailer-type" [options]="trailerTypes" [(ngModel)]="trailerTypeId" optionLabel="name" optionValue="trailerTypeId"
                            [showClear]="true" [filter]="true" filterBy="code,name" placeholder="Any trailer type" appendTo="body" styleClass="w-full"></p-select>
                        <label for="entry-trailer-type">Trailer Type</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
            <div class="field">
                <p-inputgroup>
                    <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
                    <p-iftalabel>
                        <app-logistics-project-lookup inputId="entry-logistics-project" placeholder="Any project" [(ngModel)]="logisticsProjectId"></app-logistics-project-lookup>
                        <label for="entry-logistics-project">Logistics Project</label>
                    </p-iftalabel>
                </p-inputgroup>
            </div>
        </div>
    </app-page-search-panel>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <app-page-data-panel title="Mobilization Directory" subtitle="API-backed assignment records with lifecycle actions." [resultText]="totalRecords + ' total'">
        <p-table
            [value]="rows"
            [lazy]="true"
            [paginator]="true"
            [rows]="rowsPerPage"
            [first]="first"
            [totalRecords]="totalRecords"
            [loading]="isLoading"
            [rowsPerPageOptions]="[10, 20, 50]"
            (onLazyLoad)="loadEntries($event)"
            styleClass="p-datatable-sm"
            responsiveLayout="scroll">
            <ng-template pTemplate="header">
                <tr>
                    <th>Assignment</th>
                    <th>Status</th>
                    <th>Driver</th>
                    <th>Truck</th>
                    <th>Trailer</th>
                    <th>Project/Site</th>
                    <th>Dates</th>
                    <th>Salary</th>
                    <th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
                <tr>
                    <td class="font-semibold">{{ row.mobilizationNumber || ('MOB-' + row.mobilizationId) }}</td>
                    <td><p-tag [value]="row.status || 'DRAFT'" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td>
                        <div class="stacked-cell">
                            <span>{{ row.driverName || '-' }}</span>
                            <small>{{ row.driverPhoneNumber || row.driverLicenseNumber || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="stacked-cell">
                            <span>{{ row.vehiclePlateNumber || '-' }}</span>
                            <small>{{ row.vehicleRegistrationNumber || row.vehicleTypeName || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="stacked-cell">
                            <span>{{ trailerLabel(row) }}</span>
                            <small>{{ row.trailerTypeName || row.trailerRegistrationNumber || '-' }}</small>
                        </div>
                    </td>
                    <td>
                        <div class="stacked-cell">
                            <span>{{ projectLabel(row) }}</span>
                            <small>{{ siteLabel(row) }}</small>
                        </div>
                    </td>
                    <td>{{ dateLabel(row.startDate) }} - {{ dateLabel(row.endDate) }}</td>
                    <td>
                        <div class="stacked-cell">
                            <span>{{ salaryLabel(row) }}</span>
                            <small>{{ row.salaryCalculationStatus || '-' }}</small>
                        </div>
                    </td>
                    <td class="row-actions">
                        <p-button icon="pi pi-eye" [rounded]="true" [text]="true" (onClick)="openEntry(row)" ariaLabel="View mobilization"></p-button>
                        <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary" (onClick)="editEntry(row)" ariaLabel="Edit mobilization"></p-button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="9" class="empty-cell">No mobilization entries found.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
